import React, { useState, useEffect, useCallback } from "react";
import {
  PhoneCall,
  ChevronDown,
  ChevronUp,
  Settings as SettingsIcon,
  BellOff,
  RefreshCw,
  Loader2,
  Trash2,
  Save,
  Check,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { formatDateTime } from "../utils/format";
import { useSearch, matchesQuery } from "../context/SearchContext";
import ConfirmDialog from "../components/ConfirmDialog";
import TimePicker from "../components/TimePicker";
import PropertyAutomationPicker from "../components/PropertyAutomationPicker";

export default function AiCalls() {
  const {
    communications,
    deleteCommunication,
    refreshCallTranscript,
    syncInboundCalls,
    refreshCommunications,
    properties,
    automationSettings,
    updateAutomationSettings,
  } = useData();
  const { query } = useSearch();
  const [expanded, setExpanded] = useState(new Set());
  const [showSettings, setShowSettings] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (showSettings) setDraft({ ...automationSettings });
  }, [showSettings, automationSettings]);

  const updateDraft = useCallback((patch) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    setJustSaved(false);
  }, []);

  const isDirty = draft && JSON.stringify(draft) !== JSON.stringify(automationSettings);

  const handleSave = async () => {
    if (!draft || !isDirty) return;
    setSaving(true);
    try {
      await updateAutomationSettings(draft);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch {}
    setSaving(false);
  };

  const d = draft || automationSettings;

  const calls = communications
    .filter((c) => c.type === "ai_call")
    .filter((c) =>
      matchesQuery(query, c.tenantName, c.houseNumber, c.notes, c.transcript),
    )
    .sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });

  const s = automationSettings;

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              AI Calls
            </h2>
            <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
              {calls.length} {calls.length === 1 ? "call" : "calls"}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            History of reminder calls made by the AI voice agent.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            disabled={refreshingAll}
            onClick={async () => {
              setRefreshingAll(true);
              try { await refreshCommunications(); } catch {}
              try { await syncInboundCalls(); } catch {}
              const freshCalls = communications.filter((c) => c.type === "ai_call" && c.vapiCallId && !c.transcript && c.status !== "failed" && c.status !== "skipped");
              for (const c of freshCalls) {
                try { await refreshCallTranscript(c.id); } catch {}
              }
              setRefreshingAll(false);
            }}
          >
            {refreshingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </button>
          <button
            onClick={() => setShowSettings((v) => !v)}
            className={`inline-flex items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 ${showSettings ? "bg-gray-100 dark:bg-gray-700" : ""}`}
          >
            <SettingsIcon className="h-3.5 w-3.5" />
            Settings
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="card p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Automatic AI calling
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                When enabled, the AI voice agent will automatically call overdue
                tenants to remind them about outstanding rent.
              </p>
            </div>
            <button
              onClick={() =>
                updateDraft({
                  autoCallEnabled: !d.autoCallEnabled,
                })
              }
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                d.autoCallEnabled ? "bg-indigo-600" : "bg-gray-200"
              }`}
              role="switch"
              aria-checked={d.autoCallEnabled}
              aria-label="Toggle automatic AI calling"
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                  d.autoCallEnabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          {d.autoCallEnabled && (
            <div className="mt-6 grid gap-5 border-t border-gray-100 dark:border-gray-700 pt-5 sm:grid-cols-2">
              <div>
                <label className="label-base">
                  Call tenants who are overdue by
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={d.callDaysAfterDue}
                    onChange={(e) =>
                      updateDraft({
                        callDaysAfterDue: Math.max(
                          1,
                          Math.min(30, Number(e.target.value) || 1),
                        ),
                      })
                    }
                    className="input-base w-20 text-center"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    day{d.callDaysAfterDue === 1 ? "" : "s"} or more
                  </span>
                </div>
              </div>
              <div>
                <label className="label-base">Call at</label>
                <div className="flex items-center gap-2">
                  <TimePicker
                    value={d.callTimeOfDay || "10:00"}
                    onChange={(v) => updateDraft({ callTimeOfDay: v })}
                    minHour={6}
                    maxHour={21}
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">EAT</span>
                </div>
              </div>
              <div>
                <label className="label-base">Target</label>
                <label className="mt-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={d.callOverdueOnly}
                    disabled
                    className="checkbox-base"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Overdue tenants only
                  </span>
                </label>
                <p className="mt-1 ml-6 text-xs text-gray-500 dark:text-gray-400">
                  AI calls are reserved for tenants whose rent is past due.
                </p>
              </div>
              <PropertyAutomationPicker
                properties={properties}
                includedIds={d.includedCallProperties}
                onChange={(ids) => updateDraft({ includedCallProperties: ids })}
                label="Properties to include"
                helperText="Only checked properties will receive automatic AI calls. New properties start unchecked."
              />
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-4 text-xs text-gray-600 dark:text-gray-400 sm:col-span-2">
                <strong className="text-gray-900 dark:text-white">
                  How it works:
                </strong>{" "}
                The AI voice agent will call tenants whose rent is overdue by{" "}
                {d.callDaysAfterDue} day
                {d.callDaysAfterDue === 1 ? "" : "s"} or more. A transcript and
                notes will be provided for each call. Calls happen once per day
                until the tenant pays.
              </div>
            </div>
          )}

          {!d.autoCallEnabled && (
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              <BellOff className="h-4 w-4 text-gray-400" />
              Automatic AI calling is turned off. You can still trigger
              individual or bulk calls manually from the Tenants page.
            </div>
          )}

          <div className="mt-5 flex items-center justify-end gap-3 border-t border-gray-100 dark:border-gray-700 pt-4">
            {justSaved && (
              <span className="flex items-center gap-1 text-sm font-medium text-green-600">
                <Check className="h-4 w-4" /> Saved
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className={`btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold ${
                !isDirty && !justSaved ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}

      {refreshingAll && (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-indigo-100 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950 px-4 py-3 text-sm text-indigo-700 dark:text-indigo-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Syncing calls and fetching transcripts…
        </div>
      )}

      {calls.length === 0 && !refreshingAll ? (
        <div className="card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
            <PhoneCall className="h-6 w-6" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
            No AI calls yet
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {s.autoCallEnabled
              ? "Automatic calling is enabled. Call history will appear here."
              : "Trigger an AI call reminder from the Tenants page to see records here."}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {calls.map((c) => {
            const open = expanded.has(c.id);
            const property = properties.find((p) => p.id === c.propertyId);
            return (
              <li key={c.id} className="card overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggle(c.id)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 sm:px-5 sm:py-4"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                      <PhoneCall className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {c.tenantName}
                          </div>
                          {c.phone && (
                            <div className="text-xs text-gray-400 dark:text-gray-500">{c.phone}</div>
                          )}
                        </div>
                        {open ? (
                          <ChevronUp className="h-4 w-4 flex-none text-gray-400 mt-0.5" />
                        ) : (
                          <ChevronDown className="h-4 w-4 flex-none text-gray-400 mt-0.5" />
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {property?.name || "—"} · House {c.houseNumber} · {formatDateTime(c.createdAt)}
                        {c.durationSec ? ` · ${Math.round(c.durationSec / 60)}m` : ""}
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {c.direction === "inbound" ? (
                          <span className="rounded-full bg-purple-50 dark:bg-purple-950 px-2 py-0.5 text-[11px] font-medium text-purple-700 dark:text-purple-400 ring-1 ring-inset ring-purple-600/20 dark:ring-purple-500/30">
                            inbound
                          </span>
                        ) : (
                          <span className="rounded-full bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-gray-400 ring-1 ring-inset ring-gray-500/20 dark:ring-gray-600/30">
                            outbound
                          </span>
                        )}
                        <CallStatusBadge status={c.status} />
                      </div>
                    </div>
                  </div>
                </button>
                {open && (
                  <div className="space-y-4 border-t border-gray-100 dark:border-gray-700 px-5 py-4">
                    {c.status === "failed" && (
                      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 px-3 py-2 text-sm text-red-700 dark:text-red-400">
                        Call failed.{" "}
                        {c.vapiResponse?.error
                          ? typeof c.vapiResponse.error === "string"
                            ? c.vapiResponse.error
                            : "Check the phone number and try again."
                          : "Please check the phone number and try again."}
                      </div>
                    )}
                    {c.status === "skipped" && (
                      <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                        Call skipped. {c.phone || "This number"} is not a Safaricom number. AI calls only work with Safaricom.
                      </div>
                    )}
                    {c.summary && (
                      <div>
                        <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Summary
                        </div>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {c.summary}
                        </p>
                      </div>
                    )}
                    {c.notes && (
                      <div>
                        <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Notes
                        </div>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {c.notes}
                        </p>
                      </div>
                    )}
                    {c.transcript ? (
                      <div>
                        <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Transcript
                        </div>
                        <TranscriptView text={c.transcript} />
                      </div>
                    ) : (c.status === "initiated" || c.status === "in_progress") && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Transcript will be available after the call ends.
                      </div>
                    )}
                    {c.durationSec != null && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Duration: {Math.floor(c.durationSec / 60)}m {Math.round(c.durationSec % 60)}s
                        {c.cost != null && ` · Cost: $${Number(c.cost).toFixed(4)}`}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      {c.vapiCallId && !c.transcript && c.status !== "failed" && c.status !== "skipped" ? (
                        <button
                          className="btn-secondary text-xs"
                          disabled={refreshingId === c.id}
                          onClick={async () => {
                            setRefreshingId(c.id);
                            try {
                              await refreshCallTranscript(c.id);
                            } catch (err) {
                              console.error("Refresh failed:", err);
                            }
                            setRefreshingId(null);
                          }}
                        >
                          {refreshingId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                          {refreshingId === c.id ? "Refreshing…" : "Fetch transcript"}
                        </button>
                      ) : <span />}
                      <button
                        className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-red-600 cursor-pointer"
                        onClick={() => setConfirmDelete(c)}
                        aria-label="Delete call record"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete call record?"
        description="This will remove the call record and allow the scheduler to re-call if the tenant still qualifies."
        confirmLabel="Delete"
        tone="danger"
        loading={deleting}
        onConfirm={async () => {
          setDeleting(true);
          try {
            await deleteCommunication(confirmDelete.id);
          } catch (err) {
            console.error("Delete failed:", err);
          }
          setDeleting(false);
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}

const STATUS_STYLES = {
  completed: "bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 ring-green-600/20 dark:ring-green-500/30",
  initiated: "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-400 ring-blue-600/20 dark:ring-blue-500/30",
  in_progress: "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-400 ring-blue-600/20 dark:ring-blue-500/30",
  failed: "bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-400 ring-red-600/20 dark:ring-red-500/30",
  skipped: "bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-400 ring-amber-600/20 dark:ring-amber-500/30",
};

function CallStatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.initiated;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${style}`}>
      {(status || "unknown").replace("_", " ")}
    </span>
  );
}

function TranscriptView({ text }) {
  if (!text) return null;
  // Parse transcript lines into speaker + message pairs.
  // Common formats: "AI: Hello..." or "Speaker: message" or plain lines
  const lines = text.split("\n").filter((l) => l.trim());

  return (
    <div className="space-y-2 rounded-lg bg-gray-50 dark:bg-gray-900 p-3">
      {lines.map((line, i) => {
        const match = line.match(/^(AI|Bot|Assistant|Agent|User|Customer|Human|[^:]{1,30}):\s*(.+)/i);
        if (match) {
          const speaker = match[1].trim();
          const msg = match[2].trim();
          const isAi = /^(ai|bot|assistant|agent)$/i.test(speaker);
          return (
            <div key={i} className={`flex ${isAi ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[85%] sm:max-w-[75%] rounded-xl px-3 py-2 text-sm ${
                isAi
                  ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700"
                  : "bg-indigo-600 text-white"
              }`}>
                <div className={`text-[10px] font-semibold mb-0.5 ${isAi ? "text-gray-500 dark:text-gray-400" : "text-indigo-200"}`}>
                  {speaker}
                </div>
                {msg}
              </div>
            </div>
          );
        }
        // Plain line without speaker prefix
        return (
          <div key={i} className="text-xs text-gray-500 dark:text-gray-400 text-center italic">
            {line}
          </div>
        );
      })}
    </div>
  );
}
